import { useEffect } from 'react';
import { throttle } from 'lodash';
import { useCallback } from 'react';
import { useMemo } from 'react';
import { useRef } from 'react';

/**
 * Throttle scroll event and calculate the scroll level to display or disappear certain elements (ex: header)
 * @param callback
 * @param minimumScroll
 * @param timeoutThrottle
 */

const useDocumentScrollThrottled = (
  callback: (params: {
    isScrolledDown: boolean;
    isMinimumScrolled: boolean;
  }) => void,
  headerContainerRef: React.RefObject<HTMLDivElement | null>,
  minimumScroll = 0,
  timeoutThrottle = 300,
  enabled = true,
) => {
  const previousScrollPosition = useRef(0);

  const handleDocumentScroll = useCallback(() => {
    const { scrollTop } = document.documentElement || document.body;

    const headerHeight =
      headerContainerRef.current?.getBoundingClientRect().height || 0;

    const currentScrollTop = scrollTop - headerHeight;

    const isScrolledDown = previousScrollPosition.current < currentScrollTop;
    const isMinimumScrolled = currentScrollTop > minimumScroll;

    previousScrollPosition.current = currentScrollTop;

    callback({ isScrolledDown, isMinimumScrolled });
  }, [minimumScroll, callback]);

  const handleDocumentScrollThrottled = useMemo(
    () => throttle(handleDocumentScroll, timeoutThrottle),
    [handleDocumentScroll, timeoutThrottle],
  );

  useEffect(() => {
    if (enabled) {
      window.addEventListener('scroll', handleDocumentScrollThrottled);

      return () =>
        window.removeEventListener('scroll', handleDocumentScrollThrottled);
    }
  }, [enabled, handleDocumentScrollThrottled]);
};

export default useDocumentScrollThrottled;
